import {
	type Jast,
	type Clause,
	type TerminalClause,
	type OperatorValue,
	type Query,
	CLAUSE_TYPE_TERMINAL,
	CLAUSE_TYPE_COMPOUND,
	creators,
	COMPOUND_OPERATOR_AND,
	print,
} from '@atlaskit/jql-ast';
import type { JqlData, JqlInfo } from './types';

const singleDatePattern = /^(-)?[0-9]+(m|minute|minutes|h|hour|hours|d|day|days|w|week|weeks){1}$/;

const isSingleDateValue = (str: string) => singleDatePattern.test(str.toLowerCase());

const wrapWithQuotesIfNeeded = (text: string) =>
	isSingleDateValue(text) ? text : `"${text.replace(/"/g, '\\"')}"`;

const getClauses = (ast: Jast, fieldIdentifier: string): TerminalClause[] => {
	const fieldsToReturn: TerminalClause[] = [];
	const where = ast.query?.where;
	if (where) {
		if (where.clauseType === CLAUSE_TYPE_COMPOUND && where.clauses) {
			where.clauses.forEach((clause: Clause) => {
				if (clause.clauseType === CLAUSE_TYPE_TERMINAL) {
					const fieldId = clause.field.value.toLowerCase();
					if (fieldIdentifier.toLowerCase() === fieldId) {
						fieldsToReturn.push(clause);
					}
				}
			});
		} else if (where.clauseType === CLAUSE_TYPE_TERMINAL) {
			const fieldId = where.field.value.toLowerCase();
			if (fieldIdentifier.toLowerCase() === fieldId) {
				fieldsToReturn.push(where);
			}
		}
	}
	return fieldsToReturn;
};

const createTerminalClause = ({
	fieldId,
	operator,
	value,
}: {
	fieldId: string;
	operator: OperatorValue;
	value: string;
}) =>
	creators.terminalClause(
		creators.field(fieldId),
		creators.operator(operator),
		creators.byText.valueOperand(wrapWithQuotesIfNeeded(value)),
	);

const appendOrReplaceClause = ({
	fieldId,
	newClauseData,
	clauseToReplace,
	query,
}: {
	fieldId: string;
	newClauseData: JqlInfo;
	clauseToReplace: TerminalClause | undefined;
	query: Query;
}) => {
	const { operator, operand: value } = newClauseData;
	if (clauseToReplace) {
		clauseToReplace.replace(
			createTerminalClause({
				fieldId,
				operator,
				value,
			}),
		);
	} else {
		query.appendClause(createTerminalClause({ fieldId, operator, value }), COMPOUND_OPERATOR_AND);
	}
};

const appendOrReplaceClauses = ({
	fieldId,
	jqlData,
	fieldToRenderClause,
	fieldToRenderSecondaryClause,
	query,
}: {
	fieldId: string;
	jqlData: JqlData;
	fieldToRenderClause: TerminalClause | undefined;
	fieldToRenderSecondaryClause: TerminalClause | undefined;
	query: Query;
}) => {
	const { primaryClause: primaryClauseData, secondaryClause: secondaryClauseData } = jqlData;
	if (primaryClauseData) {
		appendOrReplaceClause({
			fieldId,
			newClauseData: primaryClauseData,
			clauseToReplace: fieldToRenderClause || fieldToRenderSecondaryClause,
			query,
		});
	}

	if (secondaryClauseData) {
		appendOrReplaceClause({
			fieldId,
			newClauseData: secondaryClauseData,
			clauseToReplace: primaryClauseData
				? fieldToRenderSecondaryClause
				: fieldToRenderClause || fieldToRenderSecondaryClause,
			query,
		});
	}
};

const deleteUnnecessaryClauses = ({
	jqlData,
	fieldToRenderClause,
	fieldToRenderSecondaryClause,
}: {
	jqlData: JqlData | undefined;
	fieldToRenderClause: TerminalClause | undefined;
	fieldToRenderSecondaryClause: TerminalClause | undefined;
}) => {
	if (!jqlData || (!jqlData.primaryClause && !jqlData.secondaryClause)) {
		fieldToRenderClause && fieldToRenderClause.remove();
		fieldToRenderSecondaryClause && fieldToRenderSecondaryClause.remove();
	}

	if (jqlData && (!jqlData.primaryClause || !jqlData.secondaryClause)) {
		fieldToRenderSecondaryClause && fieldToRenderSecondaryClause.remove();
	}
};

const getNewJqlFromDoubleClausePickerJqlData = (
	fieldId: string,
	ast: Jast,
	jqlData?: JqlData,
): string => {
	if (!ast.query) {
		return ast.represents;
	}

	const [fieldToRenderClause, fieldToRenderSecondaryClause] = getClauses(ast, fieldId);

	if (jqlData !== undefined) {
		appendOrReplaceClauses({
			fieldId,
			jqlData,
			fieldToRenderClause,
			fieldToRenderSecondaryClause,
			query: ast.query,
		});
	}

	deleteUnnecessaryClauses({
		jqlData,
		fieldToRenderClause,
		fieldToRenderSecondaryClause,
	});

	return print(ast, {
		operatorCase: 'upper',
	});
};

export default getNewJqlFromDoubleClausePickerJqlData;
